import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase, FeedbackSummary as FeedbackSummaryType } from '../lib/supabase';
import { TrendingUp, Target, Lightbulb, BarChart3, Sparkles, Download, Printer, CheckCircle2 } from 'lucide-react';
import AnalyzeButton from './AnalyzeButton';
import { downloadPDF, printPDF } from '../lib/pdf-export';
import { format } from 'date-fns';
import { useToast } from '../contexts/ToastContext';

export default function FeedbackSummary() {
  const { user, profile } = useAuth();
  const [summary, setSummary] = useState<FeedbackSummaryType | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasResponses, setHasResponses] = useState(false);
  const [responseCounts, setResponseCounts] = useState({ valid: 0, pending: 0, failed: 0, total: 0 });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (!user) return;

    loadSummary();
    checkResponses();

    const channel = supabase
      .channel('feedback_responses_realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'feedback_responses',
        },
        () => {
          console.log('[FeedbackSummary] Response updated, rechecking...');
          checkResponses();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const loadSummary = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('feedback_summaries')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      setSummary(data);
    } catch (error) {
      console.error('Error loading summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkResponses = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('feedback_responses')
        .select(`
          id,
          audio_url,
          transcription,
          raw_feedback,
          processing_status,
          feedback_requests!inner(requester_id)
        `)
        .eq('feedback_requests.requester_id', user.id);

      if (error) throw error;

      if (!data) {
        setHasResponses(false);
        setResponseCounts({ valid: 0, pending: 0, failed: 0, total: 0 });
        return;
      }

      const validResponses = data.filter(r => {
        const hasTextContent = r.raw_feedback && r.raw_feedback.trim().length > 0;
        const hasCompletedAudio = r.processing_status === 'completed' && r.transcription && r.transcription.trim().length > 0;
        return hasTextContent || hasCompletedAudio;
      });

      const pendingTranscriptions = data.filter(r =>
        r.audio_url && (r.processing_status === 'pending' || r.processing_status === 'processing') && !r.raw_feedback
      ).length;

      const failedTranscriptions = data.filter(r =>
        r.audio_url && r.processing_status === 'failed' && !r.raw_feedback
      ).length;

      setResponseCounts({
        valid: validResponses.length,
        pending: pendingTranscriptions,
        failed: failedTranscriptions,
        total: data.length
      });

      setHasResponses(validResponses.length >= 3);
    } catch (error) {
      console.error('Error checking responses:', error);
    }
  };

  const handleExportPDF = () => {
    if (!summary || !profile) return;

    downloadPDF({
      summary,
      userName: profile.full_name || 'User',
      userEmail: profile.email,
      exportDate: format(new Date(), 'MMMM d, yyyy')
    });

    setSuccessMessage('Your feedback summary has been exported successfully as a PDF. You can now save or share this document.');
    setShowSuccessModal(true);
  };

  const handlePrintPDF = () => {
    if (!summary || !profile) return;

    printPDF({
      summary,
      userName: profile.full_name || 'User',
      userEmail: profile.email,
      exportDate: format(new Date(), 'MMMM d, yyyy')
    });
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#8CAFA8] border-t-transparent"></div>
        <p className="mt-4 text-[#6B7280]">Loading insights...</p>
      </div>
    );
  }

  if (!summary || summary.total_responses === 0) {
    const progress = Math.min((responseCounts.valid / 3) * 100, 100);
    const needsMore = 3 - responseCounts.valid;

    return (
      <div className="text-center py-20">
        <Sparkles className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-[#1F2937] mb-2">
          {responseCounts.valid >= 3 ? 'Ready for AI Analysis!' : 'Collecting Feedback...'}
        </h3>

        {responseCounts.valid < 3 && responseCounts.total > 0 && (
          <div className="max-w-md mx-auto mb-6">
            <div className="flex justify-between text-sm text-[#6B7280] mb-2">
              <span>{responseCounts.valid} of 3 responses ready</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
              <div
                className="bg-[#8CAFA8] h-2.5 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="space-y-2 text-sm text-[#6B7280]">
              {responseCounts.pending > 0 && (
                <p className="flex items-center justify-center gap-2">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                  {responseCounts.pending} audio response{responseCounts.pending > 1 ? 's' : ''} being transcribed
                </p>
              )}
              {responseCounts.failed > 0 && (
                <p className="flex items-center justify-center gap-2 text-red-600">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  {responseCounts.failed} transcription{responseCounts.failed > 1 ? 's' : ''} failed
                </p>
              )}
              <p className="mt-2">
                {responseCounts.pending > 0
                  ? `Waiting for transcriptions to complete...`
                  : `You need ${needsMore} more completed response${needsMore > 1 ? 's' : ''} to generate your AI summary.`
                }
              </p>
            </div>
          </div>
        )}

        {responseCounts.total === 0 && (
          <p className="text-[#6B7280] max-w-md mx-auto mb-6">
            Once you receive feedback responses, our AI will analyze them and
            generate personalized insights about your strengths and development areas.
          </p>
        )}

        {responseCounts.valid >= 3 && (
          <>
            <p className="text-[#6B7280] max-w-md mx-auto mb-6">
              You have {responseCounts.valid} feedback responses ready for AI analysis!
            </p>
            <AnalyzeButton onAnalyzed={loadSummary} />
          </>
        )}
      </div>
    );
  }

  return (
    <>
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[24px] max-w-md w-full shadow-2xl p-8 text-center">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-[#1F2937] mb-2">Thank You!</h2>
            <p className="text-[#6B7280] mb-6">
              {successMessage}
            </p>
            <button
              onClick={() => setShowSuccessModal(false)}
              className="bg-[#8CAFA8] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#7A9B94] transition"
            >
              Done
            </button>
          </div>
        </div>
      )}
      <div className="space-y-6">
      <div className="flex justify-end gap-3 mb-4">
        <button
          onClick={handlePrintPDF}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-[#4B5563] rounded-lg hover:bg-gray-50 transition font-medium"
        >
          <Printer className="w-4 h-4" />
          Print
        </button>
        <button
          onClick={handleExportPDF}
          className="flex items-center gap-2 px-4 py-2 bg-[#8CAFA8] text-white rounded-lg hover:bg-[#7A9B94] transition font-medium"
        >
          <Download className="w-4 h-4" />
          Export PDF
        </button>
      </div>

      <div className="bg-gradient-to-br from-[#8CAFA8] to-[#7A9B94] text-white rounded-[24px] p-8 shadow-[0_4px_24px_rgba(0,0,0,0.06)]">
        <div className="flex items-center gap-3 mb-4">
          <Sparkles className="w-8 h-8" />
          <h3 className="text-2xl font-semibold">AI-Generated Summary</h3>
        </div>
        <p className="text-white/95 text-lg leading-relaxed">
          {summary.ai_summary || 'Analysis in progress...'}
        </p>
        <div className="mt-6 flex items-center gap-2 text-sm text-white/80">
          <BarChart3 className="w-4 h-4" />
          <span>Based on {summary.total_responses} feedback responses</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-[16px] border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-green-100 p-2 rounded-lg">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-[#1F2937]">Key Strengths</h3>
          </div>
          <div className="space-y-4">
            {summary.strengths.length > 0 ? (
              summary.strengths.map((strength, index) => (
                <div key={index} className="border-l-4 border-green-500 pl-4 py-2">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-semibold text-[#1F2937]">{strength.name}</h4>
                    <span className="text-sm font-medium text-green-600">
                      {Math.round(strength.score * 100)}%
                    </span>
                  </div>
                  <p className="text-sm text-[#6B7280]">{strength.description}</p>
                </div>
              ))
            ) : (
              <p className="text-[#9CA3AF] italic">No strengths identified yet</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-[16px] border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-orange-100 p-2 rounded-lg">
              <Target className="w-6 h-6 text-orange-600" />
            </div>
            <h3 className="text-xl font-semibold text-[#1F2937]">Development Areas</h3>
          </div>
          <div className="space-y-4">
            {summary.development_areas.length > 0 ? (
              summary.development_areas.map((area, index) => (
                <div key={index} className="border-l-4 border-orange-500 pl-4 py-2">
                  <h4 className="font-semibold text-[#1F2937] mb-1">{area.area}</h4>
                  <p className="text-sm text-[#6B7280]">{area.suggestion}</p>
                </div>
              ))
            ) : (
              <p className="text-[#9CA3AF] italic">No development areas identified yet</p>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-[16px] border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-[#8CAFA8]/10 p-2 rounded-lg">
            <Lightbulb className="w-6 h-6 text-[#8CAFA8]" />
          </div>
          <h3 className="text-xl font-semibold text-[#1F2937]">Recurring Themes</h3>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {summary.themes.length > 0 ? (
            summary.themes.map((theme, index) => (
              <div key={index} className="bg-[#8CAFA8]/5 rounded-lg p-4 border border-[#8CAFA8]/20">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-[#1F2937]">{theme.name}</h4>
                  <span className="bg-[#8CAFA8]/20 text-[#8CAFA8] text-xs font-semibold px-2 py-1 rounded-full">
                    {theme.frequency}x
                  </span>
                </div>
                {theme.examples.length > 0 && (
                  <ul className="space-y-1">
                    {theme.examples.slice(0, 2).map((example, i) => (
                      <li key={i} className="text-sm text-[#6B7280]">
                        "{example}"
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))
          ) : (
            <p className="text-[#9CA3AF] italic col-span-2">No themes identified yet</p>
          )}
        </div>
      </div>
    </div>
    </>
  );
}
